import type { TypedDocumentString } from '@/gql/graphql'

import { useAuth } from '@/services/auth/auth.store'

import { GRAPHQL_URL } from './constant'

/* ---------------------------------- TYPES --------------------------------- */
interface GraphQLRespone<T> {
  data: T
}

interface GraphQLErrorBody {
  errors: [
    {
      message: string
      extensions: {
        code: number
      }
    },
  ]
}

/* ---------------------------------- FETCH --------------------------------- */

interface GqlFetchArgs<TResult, TVariables> {
  query: TypedDocumentString<TResult, TVariables>
  variables?: TVariables extends Record<string, never> ? undefined : TVariables
  opts?: {
    baseUrl?: string
    headers?: HeadersInit
    forceToken?: string
  }
}

async function gqlFetch<TResult, TVariables>(
  args: GqlFetchArgs<TResult, TVariables>
): Promise<TResult> {
  const { query, variables, opts = {} } = args
  const { baseUrl = GRAPHQL_URL, headers = {}, forceToken } = opts
  const token = useAuth.getState().getCookieToken()

  const input: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/graphql-response+json',
      Authorization: token ? `Bearer ${token}` : '',
      ...headers,
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  }

  const response = await fetch(baseUrl, input)

  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new HTTPError(input, response, body)
  }

  const body = (await response.json()) as GraphQLRespone<TResult>
  if ('errors' in body) {
    const err = new GraphQLError(input, response, body as GraphQLErrorBody)
    // if (err.has401()) {
    //   await useAuth.getState().logout()
    //   throw redirect({ to: '/auth/login' })
    // }
    throw err
  }

  return body.data
}

/* ---------------------------------- ERROR --------------------------------- */

class GraphQLError extends Error {
  public codes: number[] = []

  constructor(
    private input: RequestInit,
    private response: Response,
    private body: GraphQLErrorBody
  ) {
    super('GraphQL error')
    this.codes = this.body?.errors?.map((err) => err.extensions?.code) || []
    this.debug()
  }

  static is(error: unknown): error is GraphQLError {
    return error instanceof GraphQLError
  }

  get msg(): string {
    return this.body?.errors[0]?.message || 'Terjadi kesalahan'
  }

  get code(): number {
    return this.body?.errors[0]?.extensions?.code || 0
  }

  getCode(idx = 0): number {
    return this.body?.errors[idx]?.extensions?.code || 0
  }

  has401() {
    const errors = this.body.errors
    if (!errors.length) return

    const has401 = errors.some((err) => err.extensions?.code === 401)
    return has401
  }

  getErrorCode() {
    return this.body?.errors[0]?.extensions?.code
  }

  getMessage() {
    return this.body?.errors[0]?.message
  }

  debug() {
    console.log(
      JSON.stringify(
        {
          input: this.input,
          body: this.body,
        },
        null,
        2
      )
    )
  }
}

class HTTPError extends Error {
  constructor(
    private input: RequestInit,
    public response: Response,
    private body?: string
  ) {
    const message = `HTTP error ${response.status}: ${response.statusText}`
    super(message)
  }
}

export {
  type GraphQLRespone,
  type GraphQLErrorBody,
  type GqlFetchArgs,
  gqlFetch,
  GraphQLError,
  HTTPError,
}
